import { useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
    DELETE_SMART_INBOX_INTEGRATION,
    GET_SMART_INBOX_INTEGRATIONS,
    UPDATE_SMART_INBOX_INTEGRATION,
} from '../../../customer/queries.ts';
import { PERSONAL_SMART_INBOX_INTEGRATIONS, SETUP_PERSONAL_MAILBOX } from '../../../users/queries.ts';
import useAgentMailboxLinks from './useAgentMailboxLinks.ts';
import { useApplicationContext } from '../../../core/contexts/ApplicationContext.tsx';
import {
    canManageSmartInbox,
    canSeeOthersPersonalMailboxes,
    canUsePersonalSmartInbox,
    isOwnMailbox,
} from '../../../users/utils.ts';

export interface MailboxRow {
    id: string;
    name: string;
    mailInbox: string;
    type: 'shared' | 'personal';
    rulesCount: number | null;
    enabled: boolean;
    agentNames: string[];
}

export const useMailboxData = () => {
    const { user } = useApplicationContext();
    const canSeeShared = canManageSmartInbox(user);
    const canSeePersonal = canUsePersonalSmartInbox(user);
    const { data: sharedData, loading: sharedLoading } = useQuery(GET_SMART_INBOX_INTEGRATIONS, {
        fetchPolicy: 'cache-and-network',
        skip: !canSeeShared,
    });
    const { data: personalData, loading: personalLoading } = useQuery(PERSONAL_SMART_INBOX_INTEGRATIONS, {
        fetchPolicy: 'cache-and-network',
        skip: !canSeePersonal,
    });
    const { agentNamesByMailboxId } = useAgentMailboxLinks();

    const [updateSharedMailbox] = useMutation(UPDATE_SMART_INBOX_INTEGRATION, {
        refetchQueries: [{ query: GET_SMART_INBOX_INTEGRATIONS }],
    });
    const [deleteMailbox] = useMutation(DELETE_SMART_INBOX_INTEGRATION, {
        refetchQueries: [
            ...(canSeeShared ? [{ query: GET_SMART_INBOX_INTEGRATIONS }] : []),
            ...(canSeePersonal ? [{ query: PERSONAL_SMART_INBOX_INTEGRATIONS }] : []),
        ],
    });
    const [setupPersonalMailbox] = useMutation(SETUP_PERSONAL_MAILBOX, {
        refetchQueries: [{ query: PERSONAL_SMART_INBOX_INTEGRATIONS }],
    });

    const rows: MailboxRow[] = useMemo(() => {
        const sharedRows: MailboxRow[] = (sharedData?.smartInboxIntegrations || []).map((integration: any) => ({
            id: integration.id,
            name: integration.name,
            mailInbox: integration.mailInbox,
            type: 'shared',
            rulesCount: (integration.customCategories || []).length,
            enabled: integration.enabled,
            agentNames: agentNamesByMailboxId.get(integration.id) || [],
        }));

        const personalRows: MailboxRow[] = (personalData?.personalSmartInboxIntegrations || [])
            .filter((integration: any) => canSeeOthersPersonalMailboxes(user) || isOwnMailbox(user, integration))
            .map((integration: any) => ({
                id: integration.id,
                name: integration.name,
                mailInbox: integration.mailInbox,
                type: 'personal',
                rulesCount: null,
                enabled: integration.enabled,
                agentNames: agentNamesByMailboxId.get(integration.id) || [],
            }));

        return [...sharedRows, ...personalRows];
    }, [sharedData, personalData, agentNamesByMailboxId, user]);

    return {
        rows,
        loading: sharedLoading || personalLoading,
        updateSharedMailbox,
        deleteMailbox,
        setupPersonalMailbox,
    };
};

export default useMailboxData;
